/**
 * Question Type Labels Utility
 * Maps PSLE question type codes to human-readable labels
 * Used by SessionCompletionSummary to display struggled question types
 */

/**
 * PSLE question type codes mapped to display labels
 * Reference: PRD Section 2 - 9 PSLE question types
 */
export const QUESTION_TYPE_MAP: Record<string, string> = {
  type_1a: 'Direct Retrieval — Sentence Answer',
  type_1b: 'Direct Retrieval — Phrase Answer',
  type_1c: 'Direct Retrieval — List Answer',
  type_2: 'Word Identification',
  type_3a: 'Vocabulary — Single Word',
  type_3b: 'Vocabulary — Synonym Table',
  type_4: 'Cause-Effect',
  type_5: 'Sequencing / Ordering Events',
  type_6: 'Inference About Feelings',
  type_7: 'Why Questions (Intentions and Motives)',
  type_8: 'True / False with Reason',
  type_9: 'Higher-Level Inference',
  type_10: 'Pronoun / Word Reference',
  type_11: 'Yes/No with Evidence',
  // US-009 / FR-7 / DD-004: parallel twin of backend QUESTION_TYPE_DISPLAY_NAMES.
  // AC-009-4: in-app SessionCompletionSummary must show friendly labels, not bare codes.
  // CRITICAL: type_14/type_15/type_16 use em-dash (— U+2014), not hyphen-minus.
  type_12: 'Sentence Completion / Cloze Scaffold',
  type_13: 'Feeling / Trait MCQ Selection',
  type_14: 'Graphic Organiser — Flowchart',
  type_15: 'Graphic Organiser — Grid / Matrix',
  type_16: 'Graphic Organiser — Compare / Contrast',
};

/**
 * Get human-readable label for a question type code
 *
 * @param typeCode - Question type code from backend (e.g., 'type_1', 'type_7')
 * @returns Human-readable label or original code if unknown
 *
 * @example
 * ```typescript
 * getQuestionTypeLabel('type_1') // Returns 'Direct Retrieval'
 * getQuestionTypeLabel('type_7') // Returns 'Predictions'
 * getQuestionTypeLabel('unknown') // Returns 'unknown' (graceful fallback)
 * ```
 */
export function getQuestionTypeLabel(typeCode: string): string {
  return QUESTION_TYPE_MAP[typeCode] || typeCode;
}

/**
 * Map an array of type codes to human-readable labels
 *
 * @param typeCodes - Array of question type codes from backend
 * @returns Array of human-readable labels
 *
 * @example
 * ```typescript
 * getStruggledTypeLabels(['type_3', 'type_7'])
 * // Returns ['Inferential (Cause/Effect)', 'Predictions']
 *
 * getStruggledTypeLabels([]) // Returns []
 * getStruggledTypeLabels(null) // Returns []
 * ```
 */
export function getStruggledTypeLabels(typeCodes: string[] | null | undefined): string[] {
  if (!typeCodes || typeCodes.length === 0) {
    return [];
  }

  return typeCodes.map(getQuestionTypeLabel);
}
